import React from "react";
import { motion } from "framer-motion";
import { Search, Send, Paperclip, FileText, CheckCheck, Circle, Users, BarChart3 } from "lucide-react";

const convos = [
  { name: "Rohan Mehta", last: "Is the 2BHK still available?", time: "2m", tag: "Sales", unread: 2, active: true },
  { name: "Priya Nair", last: "Thanks for the update!", time: "14m", tag: "Support", unread: 0 },
  { name: "Imtiaz Ali", last: "Need invoice copy", time: "1h", tag: "Billing", unread: 1 },
  { name: "Sneha R.", last: "Booking confirmed ✅", time: "3h", tag: "Ops", unread: 0 },
];

const tagTone = {
  Sales: "bg-blue-50 text-blue-600",
  Support: "bg-violet-50 text-violet-600",
  Billing: "bg-amber-50 text-amber-600",
  Ops: "bg-green-50 text-green-600",
};

/**
 * Shared Team Inbox dashboard mockup (original E Telecom UI, demo data).
 */
export const TeamInboxMock = () => {
  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-[2rem] bg-[radial-gradient(circle_at_60%_20%,rgba(37,99,235,0.14),transparent_60%)] blur-xl" />
      <div className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-[0_24px_80px_rgba(2,6,23,0.16)]">
        {/* Top bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="ml-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500"><Users className="h-3.5 w-3.5 text-[var(--et-primary)]" /> Team Inbox · +91 98528 05552</span>
          <span className="ml-auto rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-600">4 online</span>
        </div>

        <div className="grid grid-cols-5">
          {/* Conversation list */}
          <div className="col-span-2 border-r border-slate-100">
            <div className="p-3 border-b border-slate-100">
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
                <Search className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-[11px] text-slate-400">Search conversations</span>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {convos.map((c) => (
                <div key={c.name} className={`flex items-start gap-2.5 px-3 py-2.5 ${c.active ? "bg-[rgba(37,99,235,0.05)]" : ""}`}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600 shrink-0">{c.name.split(" ").map((n) => n[0]).join("")}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[12px] font-semibold text-slate-800">{c.name}</span>
                      <span className="text-[9px] text-slate-400">{c.time}</span>
                    </div>
                    <div className="truncate text-[11px] text-slate-500">{c.last}</div>
                    <span className={`mt-1 inline-block rounded-full px-1.5 py-0.5 text-[8px] font-semibold ${tagTone[c.tag]}`}>{c.tag}</span>
                  </div>
                  {c.unread > 0 && <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#25D366] text-[9px] font-bold text-white">{c.unread}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Conversation panel */}
          <div className="col-span-3 flex flex-col">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <div>
                <div className="text-[12px] font-semibold text-slate-800">Rohan Mehta</div>
                <div className="inline-flex items-center gap-1 text-[10px] text-slate-400"><Circle className="h-2 w-2 fill-green-500 text-green-500" /> Assigned to Priya (Sales)</div>
              </div>
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[9px] font-semibold text-blue-600">Open</span>
            </div>
            <div className="flex-1 space-y-2 bg-[#EFE7DE]/40 p-3 min-h-[180px]">
              <Bubble side="in">Hi, is the 2BHK apartment still available?</Bubble>
              <Bubble side="out">Hello Rohan! Yes, it's available. Would you like to schedule a site visit?</Bubble>
              <Bubble side="in">Yes, this weekend works.</Bubble>
              <div className="flex gap-1.5 pt-1">
                <QuickChip>Sat 11 AM</QuickChip>
                <QuickChip>Sun 4 PM</QuickChip>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-slate-100">
              <FileText className="h-4 w-4 text-slate-400" />
              <Paperclip className="h-4 w-4 text-slate-400" />
              <div className="flex-1 rounded-full bg-slate-50 px-3 py-1.5 text-[11px] text-slate-400">Type a reply…</div>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--et-primary)] text-white"><Send className="h-3.5 w-3.5" /></span>
            </div>
          </div>
        </div>
      </div>

      <motion.div animate={{ y: [0, -7, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute -right-3 -bottom-3 rounded-2xl bg-white border border-slate-200 shadow-lg px-3 py-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50"><BarChart3 className="h-3.5 w-3.5 text-blue-600" /></span>
        <span className="text-[11px] font-semibold text-slate-700">Avg reply 42s</span>
      </motion.div>
    </div>
  );
};

const Bubble = ({ side, children }) => (
  <div className={`flex ${side === "out" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-[11px] shadow-sm ${side === "out" ? "rounded-tr-sm bg-[#DCF8C6] text-slate-800" : "rounded-tl-sm bg-white text-slate-700 border border-slate-100"}`}>
      {children}
      {side === "out" && <span className="ml-1 inline-flex align-middle"><CheckCheck className="h-3 w-3 text-blue-500" /></span>}
    </div>
  </div>
);

const QuickChip = ({ children }) => (
  <span className="rounded-lg border border-[var(--et-primary)]/30 bg-white px-2.5 py-1 text-[10px] font-medium text-[var(--et-primary)]">{children}</span>
);
